import { ICON_BUTTON_ICONS } from '../components/buttons/IconButton';

/**
 * Shared "open in a new tab" affordances for list rows and cards, so the two
 * gestures behave the same everywhere:
 *   - right-click     → an "Open in new tab" entry in IconButton's
 *                       `linkContextMenu` (built with `openInNewTabMenuItem`)
 *   - Cmd/Ctrl+click  → handled inline in the row's onClick via `isModifierOpen`
 *
 * The actual tab is opened by the caller (e.g. `utilsStore.openInNewTab(path)`
 * for a plain app path, or `utilsStore.openCompanyDetails` for an entity), so
 * each surface keeps ownership of how its destination URL is built.
 */

/**
 * True when a left-click carried a new-tab modifier: Cmd on macOS, Ctrl
 * elsewhere. Mirrors the guard used by the standalone company-details anchor
 * (CompanyDetailsTabs).
 * @param {{ metaKey?: boolean, ctrlKey?: boolean }} [event]
 * @returns {boolean}
 */
export const isModifierOpen = (event) => !!(event && (event.metaKey || event.ctrlKey));

/**
 * True when a click on an anchor belongs to the browser: Shift (new window), Alt (download) or a
 * button other than the primary one. A link's onClick returns early for these, without
 * preventDefault, like the company-details tab anchors (CompanyDetailsTabs). Cmd/Ctrl-clicks are
 * not included: the caller opens those itself (`openInNewTabOnModifier`).
 * @param {{ shiftKey?: boolean, altKey?: boolean, button?: number }} [event]
 * @returns {boolean}
 */
export const isBrowserLinkClick = (event) =>
  !!(event && (event.shiftKey || event.altKey || (event.button ?? 0) !== 0));

/**
 * A ready-made "Open in new tab" item for IconButton's `linkContextMenu` prop.
 * @param {() => void} onClick - opens the destination in a new tab
 * @returns {{ icon: string, text: string, onClick: () => void }}
 */
export const openInNewTabMenuItem = (onClick) => ({
  icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
  text: 'Open in new tab',
  onClick
});

/**
 * Build a click handler that opens a new tab on Cmd/Ctrl-click and otherwise
 * runs the same-tab action. Pairs with `openInNewTabMenuItem(openInNewTab)` so
 * both gestures share one `openInNewTab` thunk.
 * @param {() => void} openInNewTab - opens the destination in a new tab
 * @param {(event?: object) => void} activate - the same-tab action (navigate / show details)
 * @returns {(event?: object) => void}
 */
export const openInNewTabOnModifier = (openInNewTab, activate) => (event) => {
  if (isModifierOpen(event)) {
    openInNewTab();
    return;
  }
  activate(event);
};

/**
 * `onClick` + `linkContextMenu` for a view-details IconButton, wiring both
 * gestures at once: a plain click runs `activate`, Cmd/Ctrl-click and the
 * right-click menu run `openInNewTab`. Without `openInNewTab` the button keeps
 * its plain click and gets no menu; a button that is not `enabled` gets no menu.
 * @param {Object} options
 * @param {(event?: object) => void} options.activate - the same-tab action
 * @param {() => void} [options.openInNewTab] - opens the destination in a new tab
 * @param {boolean} [options.enabled=true] - whether the button can open anything
 * @returns {{ onClick: (event?: object) => void, linkContextMenu: Array<object> }}
 */
export const openInNewTabButtonProps = ({ activate, openInNewTab, enabled = true }) => {
  if (!openInNewTab) {
    return { onClick: activate, linkContextMenu: [] };
  }
  return {
    onClick: openInNewTabOnModifier(openInNewTab, activate),
    linkContextMenu: enabled ? [openInNewTabMenuItem(openInNewTab)] : []
  };
};
